/**
 * Fetches Firestore collection / sub-collection names for Document Studio.
 *
 * The Firestore web SDK cannot enumerate (sub)collections client-side, so the
 * config dashboard asks a backend Admin-SDK endpoint to list them. Returns [] on
 * any failure so path autocomplete degrades gracefully to the curated corpus.
 * @module fetchFirestoreCollections
 */

import { getBearerToken } from "../../../shared/api/postCall/bearerToken";
import { API_ENDPOINTS } from "../../../shared/enums/apiUrls";
import { unwrapBackendListData } from "../unwrapBackendListData";

/**
 * Lists collection ids at a Firestore path via the backend Admin SDK.
 *
 * @param path - Document path (even segments) whose sub-collections to list, or
 *   "" for the root collections.
 * @param signal - Optional abort signal for in-flight cancellation.
 * @returns Sorted collection ids, or [] on any error.
 */
export const fetchFirestoreCollections = async (
  path: string,
  signal?: AbortSignal,
): Promise<string[]> => {
  try {
    const query = new URLSearchParams({ path }).toString();
    const url = `${API_ENDPOINTS.LIST_FIRESTORE_COLLECTIONS}?${query}`;
    const bearerToken = await getBearerToken();

    const response = await fetch(url, {
      method: "GET",
      headers: {
        Authorization: `Bearer ${bearerToken}`,
      },
      ...(signal && { signal }),
    });

    if (!response.ok) {
      return [];
    }

    const body: unknown = await response.json();
    return unwrapBackendListData<string>(
      body,
      response.status,
      "Firestore collections",
    );
  } catch {
    return [];
  }
};
